import { notification } from 'antd';
import type { NotificationInstance } from 'antd/es/notification/interface';
import React, { useEffect } from 'react';

/** 泡泡类型 */
export declare type AntdMessageType = 'success' | 'error' | 'info' | 'warning' | 'open';

/**
 * notification 实例的模块级引用。
 * 由 <AntdMessageHolder /> 在根节点挂载时注入，
 * 之后 antdMessage() 即可在任何地方（含非组件模块）调用。
 */
let notificationApi: NotificationInstance | null = null;

/**
 * 在应用根节点（ConfigProvider / App 内部）渲染此组件，
 * 用于把 notification 实例注入到模块级变量。
 *
 * 用法：
 *   <ConfigProvider ...>
 *     <App>
 *       <AntdMessageHolder />
 *       <YourRoutes />
 *     </App>
 *   </ConfigProvider>
 */
export const AntdMessageHolder: React.FC = () => {
    const [api, holder] = notification.useNotification();

    // 把实例注入放到 useEffect 中，避免在渲染阶段产生副作用；
    // 组件卸载时清空，防止持有已失效的实例。
    useEffect(() => {
        notificationApi = api;
        return () => {
            notificationApi = null;
        };
    }, [api]);

    return holder;
};

/**
 * 右侧提示泡泡
 * @param type 泡泡类型
 * @param message 消息
 * @param options 选项
 */
const antdMessage = (type: AntdMessageType,
    message: string,
    options?: {
        /** 描述，支持字符串、JSX 元素等（ReactNode） */
        description?: React.ReactNode;
        /** 持续时间，默认：2秒 */
        duration?: number;
    }
) => {

    const { description, duration = 2 } = options || {};

    // 处理不同类型的 description，避免字符串被多余引号包裹，同时保留 ReactNode 能力
    let desc: React.ReactNode = undefined;
    if (React.isValidElement(description)) {
        // JSX 元素，直接传递
        desc = description;
    } else if (typeof description === 'string') {
        // 字符串原样使用，不需要 JSON.stringify
        desc = description;
    } else if (description !== null && description !== undefined) {
        // 其他对象（普通对象、数组、数字等）用 JSON.stringify 序列化，方便调试
        desc = JSON.stringify(description, null, 2);
    } else {
        // null 或 undefined 不传 description
        desc = undefined;
    }

    // Holder 尚未挂载时，回退到静态 API，避免调用直接丢失；
    // 同时给出提示，方便排查根节点是否忘了渲染 <AntdMessageHolder />。
    const api = notificationApi;
    if (!api) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(
                '[antdMessage] AntdMessageHolder 未挂载，已回退到静态 notification API。' +
                '请在根节点渲染 <AntdMessageHolder /> 以正确继承 ConfigProvider 上下文。'
            );
        }
        switch (type) {
            case 'success':
                notification.success({ message, description: desc, duration });
                break;
            case 'error':
                notification.error({  message, description: desc, duration });
                break;
            case 'info':
                notification.info({  message, description: desc, duration });
                break;
            case 'warning':
                notification.warning({  message, description: desc, duration });
                break;
            case 'open':
                notification.open({  message, description: desc, duration });
                break;
        }
        return;
    }

    switch (type) {
        case 'success':
            api.success({
                 message,
                description: desc,
                duration,
            });
            break;
        case 'error':
            api.error({
                 message,
                description: desc,
                duration
            });
            break;
        case 'info':
            api.info({
                 message,
                description: desc,
                duration
            });
            break;
        case 'warning':
            api.warning({
                 message,
                description: desc,
                duration
            });
            break;
        case 'open':
            api.open({
                 message,
                description: desc,
                duration
            });
            break;
    }
};

export default antdMessage;